<template>
	<div class="edit-row">
		<div class="field-item mod--edit-bar">
			<div
				@click="optionSet(TEMPLATE_OPTION)"
				:class="isBarTemplateActiveClassname"
				class="select field--w-175"
			>
				<div class="select__header">
					<div :class="templateMarkedClassname" class="select__value">{{ template }}</div>
					<div class="select__icon">
						<i class="fas fa-chevron-down"></i>
					</div>
				</div>
			</div>
		</div>
		<div class="quick-control flex--justify-between">
			<div
				v-for="option in NODE_EDIT_BAR_OPTIONS"
				:key="option.VALUE"
				@click="optionSet(option)"
				:class="{
					active: optionActive === option.VALUE,
					blocked:
					 isRestricted(option.TYPE) ||
					 isContentBlocked && option.VALUE === NODE_EDIT_BAR_OPTIONS.CONTENT.VALUE ||
					 isContentLinkBlocked && option.VALUE === NODE_EDIT_BAR_OPTIONS.LINK.VALUE ||
					 nodeEditingDesign && option.TYPE === NODE_EDIT_BAR_OPTIONS.DESIGN.VALUE ||
					 nodeEditingContent && option.TYPE === (NODE_EDIT_BAR_OPTIONS.CONTENT.TYPE /*||
					 	NODE_EDIT_BAR_OPTIONS.LINK.TYPE || NODE_EDIT_BAR_OPTIONS.NAVIGATION.TYPE*/)
				}"
				class="quick-control__item"
			>
				<i :class="
					[option.VALUE === NODE_EDIT_BAR_OPTIONS.LOCKED.VALUE && isNodeLocked ?
				 		option.ICON_CLOSED :
				 		option.ICON, {limited: option.VALUE === NODE_EDIT_BAR_OPTIONS.LOCKED.VALUE}]"
				></i>
				<div
					v-if="nodeEditingDesign && option.TYPE === NODE_EDIT_BAR_OPTIONS.DESIGN.VALUE"
					class="quick-control__map-user map-user map-user--small"
					:class="nodeEditingDesign.colorName"
				>{{ nodeEditingDesign.initials }}</div>
				<div
					v-if="nodeEditingContent && option.TYPE === (NODE_EDIT_BAR_OPTIONS.CONTENT.TYPE /*||
						NODE_EDIT_BAR_OPTIONS.LINK.TYPE || NODE_EDIT_BAR_OPTIONS.NAVIGATION.TYPE*/)"
					class="quick-control__map-user map-user map-user--small"
					:class="nodeEditingContent.colorName"
				>{{ nodeEditingContent.initials }}</div>
			</div>
		</div>
	</div>
	<popup-confirm
		:popup-status="popupActive"
		:content="popupMessage"
		@action-confirm="nodeRemove"
		@action-cancel="popupClose"
		:loading="loading"
	>
    <field-checkbox
        class="mt--15"
        :name="checkboxTitle"
        :use-change-emit="true"
        :checked="!removalConfirmation"
        @changeChecked="removalConfirmationChange"
    ></field-checkbox>
		<template #actions>
			<div v-if="isNodeInToc" class="toc-warning mt--15">
				<p class="toc-warning__text">{{ t('popup.confirm.tocWarning') }}</p>
				<div @click="nodeRemoveAndOpenToc" class="button bg--blue color--white mt--9">
					<i class="fas fa-list-ul mr--9"></i>
					<span>{{ t('popup.confirm.deleteAndManageToc') }}</span>
				</div>
			</div>
		</template>
  </popup-confirm>
</template>

<script>

import {NODE_EDIT_BAR_OPTIONS} from '@/models/edit-bar/NODE_EDIT_BAR_OPTIONS';
import {DASHBOARD_OPTIONS} from '@/models/DASHBOARD_OPTIONS';
import {useStore} from 'vuex';
import {EDITING_EXTENT_TYPES} from '@/models/EDITING_EXTENT_TYPES';
import {computed, inject, onMounted, onUnmounted, ref, watch} from 'vue';
import PermissionService from '@/utils/PermissionService';
import {Roles} from '@/models/Roles';
import PopupConfirm from '@/components/popup/PopupConfirm';
import usePopup from '@/utils/Popup';
import {useI18n} from 'vue-i18n';
import {TEMPLATE_VALUE} from "@/models/TEMPLATE_VALUE";
import useLoading from "@/utils/Loading";
import FieldCheckbox from "@/components/fields/FieldCheckbox.vue";
export default {
	components: {
    FieldCheckbox,
		PopupConfirm,
	},
	emits: ['optionSet'],
	setup(props, {emit}){
		const { t } = useI18n();
		const store = useStore();
		const emitter = inject('emitter');
		const {loading, loadingShow, loadingHide} = useLoading();
		// popup
		const { popupActive, popupOpen, popupClose } = usePopup();
		const popupMessage = computed(() => `${t('popup.confirm.warning', {action: t('popup.confirm.actions.deleteNode')})}`)

		const currentNodeOriginal = computed(() => store.getters['nodes/getSelectedRawNode']);
		const currentNode = computed(() => store.getters['nodes/getSelectedNode']);
		const editedNode = computed(() => store.getters['simultaneousEditing/getEditedNode']);
		
		const NODE_DESIGN = DASHBOARD_OPTIONS.CREATE.GROUPS.NODE.NODE_DESIGN;
		const NODE_CONTENT = DASHBOARD_OPTIONS.ORGANIZE.GROUPS[0].CONTENT_FORM;
		async function optionChoose(option){
			await store.dispatch('dashboardOptions/optionChoose', option);
		}
		
		// CLIENT RESTRICTIONS
		const clientRestrictions = computed(() => store.getters["clients/getRestrictions"]);
		const hasCustomDesign = computed(() => clientRestrictions.value.hasCustomDesign.value);
		const canEditContent = computed(() => clientRestrictions.value.canEditContentForm.value);
		function isRestricted(link){
			if(!clientRestrictions.value) return false;
			for(const restriction of Object.values(clientRestrictions.value)){
				if(restriction.option && restriction.option === link && !restriction.value) {
					return true;
				}
			}
		}
		
		// template
		const templates = computed(() => store.getters["design/getNodesTemplates"]);
		const template = computed(() => currentNode.value.template === '0' ? TEMPLATE_VALUE.GLOBAL.VALUE : templates.value[currentNode.value.template]?.name || TEMPLATE_VALUE.NONE.VALUE)
		const isTemplateChangesTracked = computed(() => store.getters["nodes/isTemplateChangesTracked"]);
		const templateMarkedClassname = computed(() => isTemplateChangesTracked.value ? 'marked' : '');
		const TEMPLATE_OPTION = {VALUE: 'TEMPLATE', TYPE: 'TEMPLATE', ICON: null, ACTION: null};
		const isBarTemplateActiveClassname = computed(() => optionActive.value === TEMPLATE_OPTION.VALUE ? 'selected--edit-bar' : '');
		
		const isContentLinkBlocked = computed(() => currentNode.value.contentLinkBlocked);
		const isContentBlocked = computed(() => currentNode.value.contentBlocked || !canEditContent.value);
		const nodeEditingContent = computed(() => currentNode.value.editingContent);
		const nodeEditingDesign = computed(() => currentNode.value.editingAppearance);
		const isNodeLocked = computed(() => currentNode.value.locked);

    // EDIT-BAR CONTENT STATUS
    const isQuickEditBarContentTracked = computed(() => store.getters['nodes/content/getQuickEditBarContentTrackedChanges']);
    function updateQuickEditBarContentWarning(status){
      store.dispatch('nodes/content/updateQuickEditBarContentWarning', status);
    }

    // TOC WARNING
    const tocNodeIds = computed(() => store.getters['tocWarnings/getTocNodeIds']);
    const isNodeInToc = computed(() => {
      const nodeId = currentNodeOriginal.value?.id;
      return nodeId != null && tocNodeIds.value.has(nodeId);
    });
    async function nodeRemoveAndOpenToc() {
      await nodeRemove();
      store.dispatch('settingsPopup/popupOpen', 'TABLE_OF_CONTENT');
    }

    // NODE REMOVE
    const checkboxTitle = computed(() => t('popup.confirm.showMessage'))
    const removalConfirmation = computed(() => store.getters["user/getRemovalConfirmation"])
    async function removalConfirmationChange(value){
      await store.dispatch('user/userPreferencesSet', { removalConfirmation: !value})
    }
    function checkRemovalConfirmation(){
      if(removalConfirmation.value) nodeRemovePopupOpen();
      if(!removalConfirmation.value) nodeRemove();
    }
		function nodeRemovePopupOpen(){
			if(!hasUserPermission.value) return;
			popupOpen();
		}
		async function nodeRemove(){
			const nodeId = currentNodeOriginal.value.id;
			// remove from active
			loadingShow();
			await store.dispatch('nodes/setSelectedNode', nodeId);
			await store.dispatch('nodes/removeNodeRequest', nodeId);
			await store.dispatch('nodes/removeNode', nodeId);
      await store.dispatch('nodes/content/checkNavigationForDeletedNode', nodeId);
			store.dispatch('tocWarnings/checkDeletedNodes');
			loadingHide();
			popupClose();
		}
		
		async function nodeClone(){
			if(!hasUserPermission.value) return;
			await store.dispatch('nodes/cloneNodeRequest', currentNodeOriginal.value.id);
		}
		function nodeLockedToggle(nodeId, currentStatus){
			const status = !currentStatus;
			store.dispatch('layers/locked/nodeLockedToggle', {[nodeId]: status});
		}
		
		const userRole = computed(() => store.getters["user/getCurrentUserRole"]);
		const hasUserPermission = computed(() => PermissionService.permissionCheck({userRole: userRole.value, permission: Roles.Student}))
		const hasUserPermissionClassname = computed(() => hasUserPermission.value ? "" : "blocked");
		
		const optionDashboardActive = computed(() => store.getters["dashboardOptions/getOptionActive"]);
		const isNodeDesignSettingsOpened = computed(() => optionDashboardActive.value?.VALUE === NODE_DESIGN.VALUE);
		watch(isNodeDesignSettingsOpened, (value, oldValue) => {
			if(optionActive.value === NODE_EDIT_BAR_OPTIONS.CONTENT.VALUE && value){
				optionActive.value = null;
			}
		})
		
		const optionActive = ref(null);
		function optionActiveToggle(option){
			optionActive.value = optionActive.value === option ? null : option;
		}
		function optionActiveClose() {
			optionActive.value = null;
		}
		function optionEmit(option){
			if(option) emit('optionSet', option);
			if(!option) emit('optionSet', null);
		}
		async function optionSet(option){
			if(isRestricted(option.TYPE)) return;
			if(option.TYPE === NODE_EDIT_BAR_OPTIONS.DESIGN.TYPE && nodeEditingDesign.value) return;
			if(option.TYPE === NODE_EDIT_BAR_OPTIONS.CONTENT.TYPE && nodeEditingContent.value) return;
      if(isQuickEditBarContentTracked.value) {
        updateQuickEditBarContentWarning(true);
        return;
      }
      if(option.ACTION){
				switch (option.ACTION){
					/*case NODE_EDIT_BAR_OPTIONS.DESIGN_SETTINGS.ACTION: {
						optionEmit(null);
						optionActiveToggle(null);
						// optionActiveToggle(NODE_EDIT_BAR_OPTIONS.DESIGN_SETTINGS.ACTION);
						toggleDesignSettings();
						break;
					}*/
					/*case NODE_EDIT_BAR_OPTIONS.CONTENT_SETTINGS.ACTION: {
						openContentSettings();
						break;
					}*/
					case NODE_EDIT_BAR_OPTIONS.CLONE.ACTION: {
						await nodeClone();
						break;
					}
					case NODE_EDIT_BAR_OPTIONS.DELETE.ACTION: {
            checkRemovalConfirmation();
						break;
					}
					case NODE_EDIT_BAR_OPTIONS.LOCKED.ACTION: {
						const id = currentNode.value.id;
						const status = currentNode.value.locked;
						nodeLockedToggle(id, status);
						break;
					}
				}
			} else {
				switch (option.VALUE){
					case NODE_EDIT_BAR_OPTIONS.DESIGN.VALUE: {
						const option = NODE_EDIT_BAR_OPTIONS.DESIGN.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						
						// if design already editing via edit-bar send stop editing node event
						if(editedNode.value?.lock_extent === EDITING_EXTENT_TYPES.APPEARANCE && !isNodeDesignSettingsOpened.value){
							await store.dispatch('simultaneousEditing/stopEditingNode', null);
						} else {
							const nodeData = {
								nodeId: currentNodeOriginal.value.id,
								lockExtent: EDITING_EXTENT_TYPES.APPEARANCE,
							}
							// if(isNodeDesignSettingsOpened.value) await store.dispatch('dashboardOptions/sidebarClose');
							
							await store.dispatch('simultaneousEditing/startEditingNode', nodeData)
						}
						if(isNodeDesignSettingsOpened.value){
							await store.dispatch('settingsSidebar/sidebarClose', null, { root: true});
							await store.dispatch('dashboardOptions/optionDrop');
						}
						
						break;
					}
					case NODE_EDIT_BAR_OPTIONS.CONTENT.VALUE: {
						const option = NODE_EDIT_BAR_OPTIONS.CONTENT.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						
						// if content already editing via edit-bar send stop editing node event
						if(editedNode.value?.lock_extent === EDITING_EXTENT_TYPES.CONTENT){
							await store.dispatch('simultaneousEditing/stopEditingNode', null);
						} else {
							const nodeData = {
								nodeId: currentNodeOriginal.value.id,
								lockExtent: EDITING_EXTENT_TYPES.CONTENT,
							}
							if(isNodeDesignSettingsOpened.value) await store.dispatch('dashboardOptions/sidebarClose');
							
							await store.dispatch('simultaneousEditing/startEditingNode', nodeData)
						}
						
						break;
					}
					case NODE_EDIT_BAR_OPTIONS.LINK.VALUE: {
						const option = NODE_EDIT_BAR_OPTIONS.LINK.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						checkEditing();
						break;
					}
					case NODE_EDIT_BAR_OPTIONS.VISIBILITY.VALUE: {
						const option = NODE_EDIT_BAR_OPTIONS.VISIBILITY.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						checkEditing();
						break;
					}
					case NODE_EDIT_BAR_OPTIONS.NAVIGATION.VALUE: {
						const option = NODE_EDIT_BAR_OPTIONS.NAVIGATION.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						checkEditing();
						break;
					}
					case TEMPLATE_OPTION.VALUE: {
						const option = TEMPLATE_OPTION.VALUE;
						optionActiveToggle(option);
						optionEmit(option);
						break;
					}
				}
			}
		}
		
		function checkEditing(){
			if(editedNode.value?.lock_extent === EDITING_EXTENT_TYPES.CONTENT){
				store.dispatch('simultaneousEditing/stopEditingNode', null);
			}
			if(editedNode.value?.lock_extent === EDITING_EXTENT_TYPES.APPEARANCE && !isNodeDesignSettingsOpened.value){
				store.dispatch('simultaneousEditing/stopEditingNode', null);
			}
		}
		
		function closePanel(){
			optionActiveClose();
			optionEmit();
		}
		
		onMounted(() => {
			emitter.on('closeQuickEditBarPanel', closePanel);
		})
		onUnmounted(() => {
			emitter.off('closeQuickEditBarPanel', closePanel);
		})
		
		return {
			t,
			clientRestrictions,
			loading,
			popupActive,
			popupClose,
			popupMessage,
			nodeRemove,
			NODE_EDIT_BAR_OPTIONS,
			isContentLinkBlocked,
			isContentBlocked,
			nodeEditingContent,
			nodeEditingDesign,
			isNodeLocked,
			optionSet,
			template,
			templateMarkedClassname,
			TEMPLATE_OPTION,
			isBarTemplateActiveClassname,
			isRestricted,
			optionActive,
			hasUserPermissionClassname,
      removalConfirmation,
      removalConfirmationChange,
      checkboxTitle,
			isNodeInToc,
			nodeRemoveAndOpenToc
		}
	}
}
</script>

<style lang="scss">
@use "@/scss/sections/editor/quick-edit-bar/quick-control";
</style>
